import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Gift, Eye, Play, TrendingUp, Palette, Music } from "lucide-react";

export default function GiftAnalytics() {
  const { data: gifts = [] } = useQuery({
    queryKey: ["gift_analytics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gift_pages")
        .select("id, slug, hero_name, gift_style, views_count, play_count, selected_track_version, custom_message, sender_name, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ["gift_transactions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("id, price_rub, created_at")
        .eq("package_name", "Gift-страница")
        .eq("status", "completed");
      if (error) throw error;
      return data || [];
    },
  });

  const totalGifts = gifts.length;
  const totalRevenue = transactions.reduce((s, t) => s + t.price_rub, 0);
  const totalViews = gifts.reduce((s, g) => s + (g.views_count || 0), 0);
  const totalPlays = gifts.reduce((s, g) => s + (g.play_count || 0), 0);
  const avgViews = totalGifts > 0 ? (totalViews / totalGifts).toFixed(1) : "0";
  const avgPlays = totalGifts > 0 ? (totalPlays / totalGifts).toFixed(1) : "0";

  const lightCount = gifts.filter(g => g.gift_style !== "black").length;
  const blackCount = gifts.filter(g => g.gift_style === "black").length;
  const v1Count = gifts.filter(g => (g.selected_track_version || 1) === 1).length;
  const v2Count = gifts.filter(g => g.selected_track_version === 2).length;
  const withMessage = gifts.filter(g => g.custom_message).length;
  const messageRate = totalGifts > 0 ? Math.round((withMessage / totalGifts) * 100) : 0;

  return (
    <AdminLayout title="Gift Mode Аналитика">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Gift className="w-4 h-4" /> Всего подарков
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{totalGifts}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4" /> Доход
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{totalRevenue} ₽</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Eye className="w-4 h-4" /> Ср. просмотры
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{avgViews}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Play className="w-4 h-4" /> Ср. воспроизведения
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{avgPlays}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Palette className="w-4 h-4" /> Стили
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div>
                <span className="text-xs text-muted-foreground">☀️ Light</span>
                <p className="text-lg font-bold">{lightCount}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">🖤 Black</span>
                <p className="text-lg font-bold">{blackCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Music className="w-4 h-4" /> Версии треков
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div>
                <span className="text-xs text-muted-foreground">Версия 1</span>
                <p className="text-lg font-bold">{v1Count}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Версия 2</span>
                <p className="text-lg font-bold">{v2Count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">💌 С сообщением</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{messageRate}%</p>
            <p className="text-xs text-muted-foreground">{withMessage} из {totalGifts}</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent gifts table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Последние подарки</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-left">
                  <th className="pb-2 font-medium">Получатель</th>
                  <th className="pb-2 font-medium">Стиль</th>
                  <th className="pb-2 font-medium">Версия</th>
                  <th className="pb-2 font-medium text-center">👁</th>
                  <th className="pb-2 font-medium text-center">▶️</th>
                  <th className="pb-2 font-medium">Дата</th>
                </tr>
              </thead>
              <tbody>
                {gifts.slice(0, 20).map(g => (
                  <tr key={g.id} className="border-b last:border-0">
                    <td className="py-2 font-medium">{g.hero_name}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${g.gift_style === "black" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700"}`}>
                        {g.gift_style === "black" ? "Black" : "Light"}
                      </span>
                    </td>
                    <td className="py-2 text-center">V{g.selected_track_version || 1}</td>
                    <td className="py-2 text-center">{g.views_count || 0}</td>
                    <td className="py-2 text-center">{g.play_count || 0}</td>
                    <td className="py-2 text-xs text-muted-foreground">{new Date(g.created_at).toLocaleDateString("ru")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
